// Deterministic, never-empty cover art for a challenge. Real artwork (coverImageUrl)
// overrides the generated gradient + abstract motif. Brand-anchored, dark-native.

export type ChallengeCover = {
  imageUrl?: string
  gradient: string
  motifIndex: number
  tint: string
}

// Cohesive dark-native palette families, anchored by brand green (#1fad7e).
const COVER_PALETTES: ReadonlyArray<readonly [string, string]> = [
  ['#1fad7e', '#0b4a39'],
  ['#2a6f97', '#0b2536'],
  ['#6d4ba8', '#241640'],
  ['#b0563f', '#371811'],
  ['#1f8aa0', '#0a323c'],
  ['#a07a1f', '#352609'],
  ['#9a2f57', '#330f1f'],
  ['#3f7bb0', '#13243a'],
]

export const COVER_MOTIF_COUNT = 6

// The stored coverImageUrl is a "ghost" base path that is never loaded as-is;
// both rendered variants are derived from it by their pixel width. Admins upload
// the two sized files manually. Keeping the size in the filename (not a query
// param or CSS guess) makes a wrong/missing upload visible as a broken preview.
export const COVER_IMAGE_SIZES = { sm: 160, lg: 1200 } as const
export type CoverImageSize = keyof typeof COVER_IMAGE_SIZES

// Convert a source-image vertical focal point (0-100, 0 = top of the image,
// 100 = bottom) into the CSS object-position Y% for one specific container.
//
// With `object-fit: cover`, a raw object-position percentage is relative to the
// *overflow*, which differs per container aspect ratio — so the same percentage
// crops a different band on the card, the drawer, and the admin preview. This
// normalizes against the container and image aspect ratios so the chosen focal
// point of the source image stays centered everywhere (and stays put when a
// container is responsive). f = 0.5 always maps to 50%; the ends clamp to the
// image's top/bottom edge.
export const computeCoverObjectPositionY = (
  focusPct: number,
  containerAspect: number, // container width / height
  imageAspect: number,     // natural image width / height
): number => {
  const f = Math.min(100, Math.max(0, focusPct)) / 100
  // k = scaled image height / container height. k > 1 means the image overflows
  // vertically (the only case where vertical position matters).
  const k = containerAspect / imageAspect
  if (!Number.isFinite(k) || k <= 1) return 50
  const frac = (f * k - 0.5) / (k - 1)
  return Math.min(1, Math.max(0, frac)) * 100
}

// `dir/name.webp` + 160 → `dir/name-160.webp`. Any query string (e.g. CDN
// cache-buster) is preserved after the suffix.
export const toCoverVariantUrl = (base: string, px: number): string => {
  const [path, query] = base.split('?', 2)
  const dot = path.lastIndexOf('.')
  const variant = dot === -1 ? `${path}-${px}` : `${path.slice(0, dot)}-${px}${path.slice(dot)}`
  return query === undefined ? variant : `${variant}?${query}`
}

// djb2 string hash → stable non-negative integer.
const hashString = (value: string): number => {
  let hash = 5381
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0
  }
  return Math.abs(hash)
}

export const getChallengeCover = (input: {
  taskId: string
  coverImageUrl?: string
  // Which derived variant to load. Thumbnail surfaces pass 'sm'; defaults to
  // the full-size 'lg'. Both are derived from the base coverImageUrl.
  size?: CoverImageSize
}): ChallengeCover => {
  const hash = hashString(input.taskId)
  const palette = COVER_PALETTES[hash % COVER_PALETTES.length]
  const base =
    input.coverImageUrl !== undefined && input.coverImageUrl !== null && input.coverImageUrl !== ''
      ? input.coverImageUrl
      : undefined
  const imageUrl =
    base !== undefined ? toCoverVariantUrl(base, COVER_IMAGE_SIZES[input.size ?? 'lg']) : undefined
  return {
    imageUrl,
    gradient: `linear-gradient(135deg, ${palette[0]}, ${palette[1]})`,
    motifIndex: Math.floor(hash / COVER_PALETTES.length) % COVER_MOTIF_COUNT,
    tint: palette[0],
  }
}
